import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { JobPost } from '~/api/jobPosts/types';

import { useUpdateJobPostStatus } from '~/api/jobPosts/useUpdateJobPostStatus';
import { AlertDialog } from '~/components';
import { toast } from '~/utils/toast';

export const useOnFilledJobPost = (jobPost: JobPost) => {
  const { t } = useTranslation(['common', 'jobPosts']);
  const { isPending, mutate } = useUpdateJobPostStatus(jobPost.id);
  const filledHandle = AlertDialog.useHandle();

  const onFilled = useCallback(() => filledHandle.open(null), [filledHandle]);

  const confirmFilled = useCallback(() => {
    mutate('fill', {
      onSuccess: () => {
        filledHandle.close();
        toast.success({ description: t('jobPosts:alerts.successFilled') });
      },
      onError: () => toast.danger({ description: t('common:error') }),
    });
  }, [filledHandle, mutate, t]);

  return { confirmFilled, filledHandle, isPending, onFilled };
};

export const useOnCancelledJobPost = (jobPost: JobPost) => {
  const { t } = useTranslation(['common', 'jobPosts']);
  const { isPending, mutate } = useUpdateJobPostStatus(jobPost.id);
  const cancelledHandle = AlertDialog.useHandle();

  const onCancelled = useCallback(() => cancelledHandle.open(null), [cancelledHandle]);

  const confirmCancelled = useCallback(() => {
    mutate('cancel', {
      onSuccess: () => {
        cancelledHandle.close();
        toast.success({ description: t('jobPosts:alerts.successCancelled') });
      },
      onError: () => toast.danger({ description: t('common:error') }),
    });
  }, [cancelledHandle, mutate, t]);

  return { cancelledHandle, confirmCancelled, isPending, onCancelled };
};
